import { useState } from 'react';

function fmt(n) {
  return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ROOF_TYPES = [
  { label: 'Overlay', sellRate: 695, laborRate: 165 },
  { label: 'Single Tear-Off', sellRate: 875, laborRate: 210 },
  { label: 'Two Layer Tear-Off', sellRate: 1188, laborRate: 210 },
  { label: 'Three Layer (Custom)', sellRate: null, laborRate: 210 },
];

const MATERIAL_TYPES = [
  { label: 'Owens Corning', rate: 290 },
  { label: 'IKO', rate: 160 },
  { label: 'Cheap Material', rate: 160 },
];

const PAINT_CONTRACTORS = [
  { label: 'Rolando', sellRate: 200, costRate: 175 },
  { label: 'David', sellRate: 175, costRate: 160 },
  { label: 'Eddie / Snoop', sellRate: 180, costRate: 165 },
];

function Card({ title, color, icon, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ background: color, color: '#fff', padding: '14px 20px', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{icon}</span> {title}
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>{hint}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

function Num({ value, onChange, width = 90, prefix, suffix }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {prefix && <span style={{ fontSize: 13, color: '#666' }}>{prefix}</span>}
      <input type="number" min="0" value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        style={{ width, padding: '7px 10px', border: '1.5px solid #ddd', borderRadius: 7, fontSize: 14, textAlign: 'center', outline: 'none' }} />
      {suffix && <span style={{ fontSize: 13, color: '#666' }}>{suffix}</span>}
    </div>
  );
}

function Sel({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding: '7px 10px', border: '1.5px solid #ddd', borderRadius: 7, fontSize: 13, flex: 1, minWidth: 200 }}>
      {options.map((o, i) => <option key={i} value={i}>{o}</option>)}
    </select>
  );
}

function CostOnlyBadge({ label, amount }) {
  if (!amount) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#fff8f8', borderRadius: 6, marginTop: 4, fontSize: 13 }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ color: '#c62828', fontWeight: 600 }}>–{fmt(amount)} (cost)</span>
    </div>
  );
}

function ExtraCharge({ label, hint, sellPerSq, costPerSq, squares, value, onChange, perUnit = false, unitLabel = 'sq' }) {
  const sell = perUnit ? sellPerSq * value : sellPerSq * squares * value;
  const cost = perUnit ? costPerSq * value : costPerSq * squares * value;
  return (
    <div style={{ padding: '12px 14px', background: '#f9f9f9', borderRadius: 8, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{label}</div>
          {hint && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{hint}</div>}
        </div>
        <Num value={value} onChange={onChange} width={70} suffix={unitLabel} />
      </div>
      {value > 0 && (
        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
          <span style={{ color: '#1565C0' }}>+{fmt(sell)} sell</span>
          <span style={{ color: '#c62828' }}>+{fmt(cost)} cost</span>
          <span style={{ color: '#1a355f', fontWeight: 600 }}>= {fmt(sell - cost)} profit</span>
        </div>
      )}
    </div>
  );
}

export default function JobCalculator({ embedded = false, onSaveResults = null, savedValues = null } = {}) {
  const [tab, setTab] = useState('roof');

  // Roofing
  const [roofTypeIdx, setRoofTypeIdx] = useState(0);
  const [squares, setSquares] = useState(0);
  const [customRate, setCustomRate] = useState('');
  const [materialIdx, setMaterialIdx] = useState(0);
  const [woodFt, setWoodFt] = useState(0);
  const [permit, setPermit] = useState(850);
  const [dumpsterQty, setDumpsterQty] = useState(1);
  const [dumpsterCost, setDumpsterCost] = useState(400);
  const [extraLayers, setExtraLayers] = useState(0);
  const [doubleHandle, setDoubleHandle] = useState(false);
  const [steepPitch, setSteepPitch] = useState(false);
  const [osbSheets, setOsbSheets] = useState(0);
  const [solarPanels, setSolarPanels] = useState(0);

  // EC
  const [ecSquares, setEcSquares] = useState(0);
  const [ecExtraFloats, setEcExtraFloats] = useState(0);

  // Paint
  const [paintIdx, setPaintIdx] = useState(0);
  const [paintSquares, setPaintSquares] = useState(0);

  // ── Shared across all tabs: commissions + misc (manual $, added to cost) ──
  const [commSalesRep, setCommSalesRep] = useState(0);
  const [commCanvasser, setCommCanvasser] = useState(0);
  const [commTelemarketer, setCommTelemarketer] = useState(0);
  const [miscCost, setMiscCost] = useState(0);
  const commTotal = (parseFloat(commSalesRep) || 0) + (parseFloat(commCanvasser) || 0) + (parseFloat(commTelemarketer) || 0);
  const extraShared = commTotal + (parseFloat(miscCost) || 0);

  // ── New service tabs (manual sell + cost rate) ──
  const [gutterFt, setGutterFt] = useState(0);
  const [gutterSell, setGutterSell] = useState(0);
  const [insulType, setInsulType] = useState(0); // 0 = blow-in $1, 1 = remove/blow-in $2.25
  const [insulSqft, setInsulSqft] = useState(0);
  const [insulSell, setInsulSell] = useState(0);
  const [winSell, setWinSell] = useState(0);
  const [winCost, setWinCost] = useState(0); // manual
  const [cementSqft, setCementSqft] = useState(0);
  const [cementSell, setCementSell] = useState(0);

  // ── ROOFING CALC ──
  const rt = ROOF_TYPES[roofTypeIdx];
  const sellRate = customRate !== '' ? parseFloat(customRate) || 0 : (rt.sellRate || 0);
  const mat = MATERIAL_TYPES[materialIdx];

  // Sell = base rate + extras that add to sell price
  let roofSell = sellRate * squares;
  roofSell += extraLayers * 45 * squares;
  if (doubleHandle) roofSell += 35 * squares;
  if (steepPitch) roofSell += 25 * squares;
  roofSell += osbSheets * 150;
  roofSell += solarPanels * 250;

  // Cost = labor + material + consumables
  let roofCost = rt.laborRate * squares;
  roofCost += mat.rate * squares;
  roofCost += woodFt * 8;
  roofCost += permit;
  roofCost += dumpsterQty * dumpsterCost;
  roofCost += extraLayers * 35 * squares; // extra layer contractor cost
  if (doubleHandle) roofCost += 20 * squares;
  if (steepPitch) roofCost += 15 * squares;
  roofCost += osbSheets * 90;
  roofCost += solarPanels * 0; // TBD

  // ── EC CALC ──
  const ecSell = 525 * ecSquares;
  const ecCost = (180 + 90) * ecSquares + ecExtraFloats * 200;

  // ── PAINT CALC ──
  const pc = PAINT_CONTRACTORS[paintIdx];
  const paintSell = pc.sellRate * paintSquares;
  const paintCost = pc.costRate * paintSquares;

  // ── NEW SERVICE CALCS ──
  const INSUL_RATES = [{ label: 'Blow-in only', rate: 1 }, { label: 'Remove / clean / blow-in', rate: 2.25 }];
  const gutterSellV = parseFloat(gutterSell) || 0;
  const gutterCost = 7.50 * (parseFloat(gutterFt) || 0);
  const insulSellV = parseFloat(insulSell) || 0;
  const insulCost = INSUL_RATES[insulType].rate * (parseFloat(insulSqft) || 0);
  const winSellV = parseFloat(winSell) || 0;
  const winCostV = parseFloat(winCost) || 0;
  const cementSellV = parseFloat(cementSell) || 0;
  const cementCost = 12 * (parseFloat(cementSqft) || 0);

  // ── ACTIVE TOTALS ──
  const baseSell = (tab === 'roof' ? roofSell : tab === 'ec' ? ecSell : tab === 'paint' ? paintSell
    : tab === 'gutters' ? gutterSellV : tab === 'insulation' ? insulSellV : tab === 'windows' ? winSellV : cementSellV);
  const baseCost = (tab === 'roof' ? roofCost : tab === 'ec' ? ecCost : tab === 'paint' ? paintCost
    : tab === 'gutters' ? gutterCost : tab === 'insulation' ? insulCost : tab === 'windows' ? winCostV : cementCost);
  const activeSell = baseSell;
  const activeCost = baseCost + extraShared; // commissions + misc added to cost on every tab
  const activeProfit = activeSell - activeCost;
  const marginPct = activeSell > 0 ? (activeProfit / activeSell * 100).toFixed(1) : '0.0';

  return (
    <>
      {!embedded && (
        <div className="page-header">
          <h2>Job Cost Calculator</h2>
          <div style={{ fontSize: 13, color: '#888' }}>Estimate sell price, job cost & profit for any service</div>
        </div>
      )}

      <div className="page-body">

        {/* Service tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #eee', marginBottom: 24 }}>
          {[['roof', '🏠 Roofing'], ['ec', '🎨 Ext Coating'], ['paint', '🖌️ Paint'], ['gutters', '🌧️ Gutters'], ['insulation', '🧊 Insulation'], ['windows', '🪟 Windows'], ['cement', '🧱 Cement']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding: '10px 24px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14,
                fontWeight: tab === key ? 700 : 400, color: tab === key ? '#0d1f3b' : '#888',
                borderBottom: tab === key ? '3px solid #0d1f3b' : '3px solid transparent', marginBottom: -2 }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Left — inputs */}
          <div style={{ flex: 2, minWidth: 340 }}>

            {/* ── ROOFING ── */}
            {tab === 'roof' && (
              <>
                <Card title="Roof Type & Size" color="#0d1f3b" icon="📐">
                  <Field label="Roof Type" hint="Select the type of installation — this sets the base sell price">
                    <Sel value={roofTypeIdx} onChange={v => { setRoofTypeIdx(parseInt(v)); setCustomRate(''); }}
                      options={ROOF_TYPES.map(r => r.sellRate ? `${r.label} — sells at $${r.sellRate}/sq` : `${r.label} — enter custom rate`)} />
                  </Field>

                  {rt.sellRate === null && (
                    <Field label="Custom Sell Rate" hint="Enter agreed sell price per square">
                      <Num value={customRate || 0} onChange={v => setCustomRate(v)} prefix="$" suffix="/sq" />
                    </Field>
                  )}

                  <Field label="Number of Squares" hint="1 square = 100 sq ft of roof area">
                    <Num value={squares} onChange={setSquares} suffix="squares" width={100} />
                    {squares > 0 && sellRate > 0 && (
                      <div style={{ fontSize: 13, color: '#1565C0', fontWeight: 600 }}>
                        Base sell: {fmt(sellRate * squares)} ({squares} sq × ${sellRate})
                      </div>
                    )}
                  </Field>
                </Card>

                <Card title="Job Costs (not added to sell price)" color="#c62828" icon="💰">
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                    These are your costs — they reduce profit but don't appear in the sell price
                  </div>

                  <Field label="Material" hint="Shingle/material cost per square">
                    <Sel value={materialIdx} onChange={v => setMaterialIdx(parseInt(v))}
                      options={MATERIAL_TYPES.map(m => `${m.label} — $${m.rate}/sq`)} />
                    {squares > 0 && <span style={{ fontSize: 13, color: '#c62828' }}>= {fmt(mat.rate * squares)}</span>}
                  </Field>

                  <Field label="Wood Replacement" hint="Linear feet of fascia/wood replacement at $8/ft">
                    <Num value={woodFt} onChange={setWoodFt} suffix="linear ft" />
                    {woodFt > 0 && <span style={{ fontSize: 13, color: '#c62828' }}>= {fmt(woodFt * 8)}</span>}
                  </Field>

                  <Field label="Permit Cost" hint="Average permit fee for this job">
                    <Num value={permit} onChange={setPermit} prefix="$" />
                  </Field>

                  <Field label="Dumpsters" hint="Number of dumpsters needed and cost each">
                    <Num value={dumpsterQty} onChange={setDumpsterQty} suffix="×" width={60} />
                    <Num value={dumpsterCost} onChange={setDumpsterCost} prefix="$" suffix="/each" />
                    {dumpsterQty > 0 && <span style={{ fontSize: 13, color: '#c62828' }}>= {fmt(dumpsterQty * dumpsterCost)}</span>}
                  </Field>

                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#fff8f8', borderRadius: 8, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, color: '#c62828', marginBottom: 6 }}>Fixed Costs Summary</div>
                    <CostOnlyBadge label={`Labor (${rt.laborRate}/sq × ${squares} sq)`} amount={rt.laborRate * squares} />
                    <CostOnlyBadge label={`Material (${mat.rate}/sq × ${squares} sq)`} amount={mat.rate * squares} />
                    {woodFt > 0 && <CostOnlyBadge label={`Wood (${woodFt} ft × $8)`} amount={woodFt * 8} />}
                    <CostOnlyBadge label="Permit" amount={permit} />
                    {dumpsterQty > 0 && <CostOnlyBadge label={`Dumpsters (${dumpsterQty}×$${dumpsterCost})`} amount={dumpsterQty * dumpsterCost} />}
                  </div>
                </Card>

                <Card title="Extra Charges (added to sell price)" color="#1565C0" icon="➕">
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                    These add to the sell price AND have a contractor cost — enter quantity/count
                  </div>

                  <ExtraCharge label="Additional Tear-Off Layers" hint="Each extra layer beyond standard — $45/sq sell, $35/sq cost"
                    sellPerSq={45} costPerSq={35} squares={squares} value={extraLayers} onChange={setExtraLayers}
                    unitLabel="extra layers" />

                  <div style={{ padding: '12px 14px', background: '#f9f9f9', borderRadius: 8, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Double Handle</div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Difficult access — $35/sq sell, $20/sq cost</div>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={doubleHandle} onChange={e => setDoubleHandle(e.target.checked)}
                          style={{ width: 18, height: 18, accentColor: '#1565C0', cursor: 'pointer' }} />
                        <span style={{ fontSize: 13, color: '#555' }}>{doubleHandle ? 'Yes' : 'No'}</span>
                      </label>
                    </div>
                    {doubleHandle && squares > 0 && (
                      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
                        <span style={{ color: '#1565C0' }}>+{fmt(35 * squares)} sell</span>
                        <span style={{ color: '#c62828' }}>+{fmt(20 * squares)} cost</span>
                        <span style={{ color: '#1a355f', fontWeight: 600 }}>= {fmt(15 * squares)} profit</span>
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '12px 14px', background: '#f9f9f9', borderRadius: 8, marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Steep Pitch (6/12+)</div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>$25/sq sell, $15/sq cost</div>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={steepPitch} onChange={e => setSteepPitch(e.target.checked)}
                          style={{ width: 18, height: 18, accentColor: '#1565C0', cursor: 'pointer' }} />
                        <span style={{ fontSize: 13, color: '#555' }}>{steepPitch ? 'Yes' : 'No'}</span>
                      </label>
                    </div>
                    {steepPitch && squares > 0 && (
                      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
                        <span style={{ color: '#1565C0' }}>+{fmt(25 * squares)} sell</span>
                        <span style={{ color: '#c62828' }}>+{fmt(15 * squares)} cost</span>
                        <span style={{ color: '#1a355f', fontWeight: 600 }}>= {fmt(10 * squares)} profit</span>
                      </div>
                    )}
                  </div>

                  <ExtraCharge label="OSB Sheeting" hint="$150/sheet sell price, $90/sheet cost"
                    sellPerSq={150} costPerSq={90} squares={squares} value={osbSheets} onChange={setOsbSheets}
                    perUnit unitLabel="sheets" />

                  <ExtraCharge label="Solar Panel Detach & Reset" hint="$250/panel sell — cost varies by electrician"
                    sellPerSq={250} costPerSq={0} squares={squares} value={solarPanels} onChange={setSolarPanels}
                    perUnit unitLabel="panels" />
                </Card>
              </>
            )}

            {/* ── EC ── */}
            {tab === 'ec' && (
              <Card title="Exterior Coating" color="#1565C0" icon="🎨">
                <div style={{ background: '#e3f2fd', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1565C0' }}>
                  <strong>Base rate: $525/sq</strong><br/>
                  Includes: 100 ft wood replacement, all trim, 3 SQ of 2 Float<br/>
                  Contractor cost: $180/sq labor + $90/sq material
                </div>
                <Field label="Number of Squares" hint="Total squares to be coated">
                  <Num value={ecSquares} onChange={setEcSquares} suffix="squares" width={100} />
                  {ecSquares > 0 && <span style={{ fontSize: 13, color: '#1565C0', fontWeight: 600 }}>Sell: {fmt(525 * ecSquares)}</span>}
                </Field>
                <Field label="Extra Floats" hint="Beyond the included floats — $200/float contractor cost">
                  <Num value={ecExtraFloats} onChange={setEcExtraFloats} suffix="extra floats" width={80} />
                  {ecExtraFloats > 0 && <span style={{ fontSize: 13, color: '#c62828' }}>+{fmt(ecExtraFloats * 200)} cost</span>}
                </Field>
              </Card>
            )}

            {/* ── PAINT ── */}
            {tab === 'paint' && (
              <Card title="Paint" color="#6a1a9a" icon="🖌️">
                <Field label="Contractor" hint="Select which contractor — each has different rates">
                  <Sel value={paintIdx} onChange={v => setPaintIdx(parseInt(v))}
                    options={PAINT_CONTRACTORS.map(p => `${p.label} — sell $${p.sellRate}/sq, cost $${p.costRate}/sq`)} />
                </Field>
                <Field label="Number of Squares" hint="Total squares to be painted">
                  <Num value={paintSquares} onChange={setPaintSquares} suffix="squares" width={100} />
                  {paintSquares > 0 && (
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: '#1565C0' }}>Sell: {fmt(pc.sellRate * paintSquares)}</span>
                      <span style={{ color: '#c62828', marginLeft: 12 }}>Cost: {fmt(pc.costRate * paintSquares)}</span>
                    </div>
                  )}
                </Field>
              </Card>
            )}

            {/* ── GUTTERS ── */}
            {tab === 'gutters' && (
              <Card title="Gutters" color="#0277bd" icon="🌧️">
                <Field label="Sell Price" hint="Total sell price for the gutter job (you set this)">
                  <Num value={gutterSell} onChange={setGutterSell} prefix="$" width={120} />
                </Field>
                <Field label="Linear Feet" hint="Cost is $7.50/ft (labor + material)">
                  <Num value={gutterFt} onChange={setGutterFt} suffix="ft" width={100} />
                  {gutterFt > 0 && <span style={{ fontSize: 13, color: '#c62828' }}>Cost: {fmt(gutterCost)} ({gutterFt} ft × $7.50)</span>}
                </Field>
              </Card>
            )}

            {/* ── INSULATION ── */}
            {tab === 'insulation' && (
              <Card title="Insulation" color="#00838f" icon="🧊">
                <Field label="Sell Price" hint="Total sell price for the insulation job (you set this)">
                  <Num value={insulSell} onChange={setInsulSell} prefix="$" width={120} />
                </Field>
                <Field label="Type" hint="Blow-in $1/sqft, or Remove/clean/blow-in $2.25/sqft">
                  <Sel value={insulType} onChange={v => setInsulType(parseInt(v))}
                    options={INSUL_RATES.map(r => `${r.label} — $${r.rate}/sqft`)} />
                </Field>
                <Field label="Square Feet" hint="Area to be insulated">
                  <Num value={insulSqft} onChange={setInsulSqft} suffix="sqft" width={100} />
                  {insulSqft > 0 && <span style={{ fontSize: 13, color: '#c62828' }}>Cost: {fmt(insulCost)} ({insulSqft} sqft × ${INSUL_RATES[insulType].rate})</span>}
                </Field>
              </Card>
            )}

            {/* ── WINDOWS ── */}
            {tab === 'windows' && (
              <Card title="Windows" color="#3949ab" icon="🪟">
                <Field label="Sell Price" hint="Total sell price for the window job (you set this)">
                  <Num value={winSell} onChange={setWinSell} prefix="$" width={120} />
                </Field>
                <Field label="Job Cost" hint="Enter the total window cost manually">
                  <Num value={winCost} onChange={setWinCost} prefix="$" width={120} />
                </Field>
              </Card>
            )}

            {/* ── CEMENT ── */}
            {tab === 'cement' && (
              <Card title="Cement" color="#5d4037" icon="🧱">
                <Field label="Sell Price" hint="Total sell price for the cement job (you set this)">
                  <Num value={cementSell} onChange={setCementSell} prefix="$" width={120} />
                </Field>
                <Field label="Square Feet" hint="Cost is $12/sqft">
                  <Num value={cementSqft} onChange={setCementSqft} suffix="sqft" width={100} />
                  {cementSqft > 0 && <span style={{ fontSize: 13, color: '#c62828' }}>Cost: {fmt(cementCost)} ({cementSqft} sqft × $12)</span>}
                </Field>
              </Card>
            )}

            {/* ── COMMISSIONS + MISC (all tabs) ── */}
            <Card title="Commissions & Misc (added to cost)" color="#b8860b" icon="💵">
              <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                Manually entered. These add to job cost and reduce profit on every service.
              </div>
              <Field label="Sales Rep Commission"><Num value={commSalesRep} onChange={setCommSalesRep} prefix="$" width={120} /></Field>
              <Field label="Canvasser Commission"><Num value={commCanvasser} onChange={setCommCanvasser} prefix="$" width={120} /></Field>
              <Field label="Telemarketer Commission"><Num value={commTelemarketer} onChange={setCommTelemarketer} prefix="$" width={120} /></Field>
              <Field label="Miscellaneous" hint="Any other cost not captured above"><Num value={miscCost} onChange={setMiscCost} prefix="$" width={120} /></Field>
              {extraShared > 0 && (
                <div style={{ fontSize: 13, color: '#c62828', fontWeight: 600, marginTop: 6 }}>
                  Added to cost: {fmt(extraShared)}{commTotal > 0 ? ` (commissions ${fmt(commTotal)})` : ''}
                </div>
              )}
            </Card>
          </div>

          {/* Right — results */}
          <div style={{ flex: 1, minWidth: 280, position: 'sticky', top: 80 }}>
            {/* Big numbers */}
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 20, marginBottom: 16 }}>
              {embedded ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Estimated Job Cost</div>
                  <div style={{ fontSize: 36, fontWeight: 700, color: '#c62828' }}>{fmt(activeCost)}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>Enter the sale price on the record to see profit.</div>
                </div>
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Sell Price to Customer</div>
                    <div style={{ fontSize: 36, fontWeight: 700, color: '#1565C0' }}>{fmt(activeSell)}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <div style={{ background: '#fff8f8', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Job Cost</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#c62828' }}>{fmt(activeCost)}</div>
                    </div>
                    <div style={{ background: activeProfit >= 0 ? '#f0f3f7' : '#fff8f8', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Profit</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: activeProfit >= 0 ? '#1a355f' : '#c62828' }}>{fmt(activeProfit)}</div>
                    </div>
                  </div>
                  <div style={{ background: parseFloat(marginPct) >= 25 ? '#e8edf5' : parseFloat(marginPct) >= 15 ? '#fff8e1' : '#ffebee',
                    borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Profit Margin</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: parseFloat(marginPct) >= 25 ? '#1a355f' : parseFloat(marginPct) >= 15 ? '#F57F17' : '#c62828' }}>
                      {marginPct}%
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      {parseFloat(marginPct) >= 25 ? '✅ Good margin' : parseFloat(marginPct) >= 15 ? '⚠️ Acceptable' : '❌ Low margin'}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Save to Job — only shown when embedded inside a job form */}
            {onSaveResults && (
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 16, marginBottom: 16 }}>
                <button type="button"
                  onClick={() => onSaveResults({ sell: activeSell, cost: activeCost, profit: activeProfit })}
                  style={{ width: '100%', padding: '12px', border: 'none', borderRadius: 8, background: '#0d1f3b',
                    color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                  💾 Save cost to job
                </button>
                <div style={{ fontSize: 11, color: '#888', marginTop: 8, textAlign: 'center' }}>
                  Saves the estimated job cost onto this record
                </div>
                {savedValues && savedValues.cost ? (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee', fontSize: 12, color: '#555' }}>
                    <div style={{ fontWeight: 700, color: '#0d1f3b', marginBottom: 4 }}>Currently saved on this job:</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}><span>Job Cost</span><span>{fmt(parseFloat(savedValues.cost) || 0)}</span></div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Cost breakdown */}
            {tab === 'roof' && squares > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 16 }}>
                <div style={{ fontWeight: 700, color: '#0d1f3b', marginBottom: 12, fontSize: 14 }}>Cost Breakdown</div>
                {[
                  [`Labor (${rt.laborRate}/sq)`, rt.laborRate * squares],
                  [`Material (${mat.label})`, mat.rate * squares],
                  woodFt > 0 && [`Wood (${woodFt} ft)`, woodFt * 8],
                  [`Permit`, permit],
                  dumpsterQty > 0 && [`Dumpsters`, dumpsterQty * dumpsterCost],
                  extraLayers > 0 && [`Extra Layers (contractor)`, extraLayers * 35 * squares],
                  doubleHandle && [`Double Handle (contractor)`, 20 * squares],
                  steepPitch && [`Steep Pitch (contractor)`, 15 * squares],
                  osbSheets > 0 && [`OSB (${osbSheets} sheets)`, osbSheets * 90],
                ].filter(Boolean).map(([label, amount]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
                    <span style={{ color: '#666' }}>{label}</span>
                    <span style={{ color: '#c62828', fontWeight: 600 }}>{fmt(amount)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 14, fontWeight: 700, borderTop: '2px solid #0d1f3b', marginTop: 6 }}>
                  <span>Total Cost</span>
                  <span style={{ color: '#c62828' }}>{fmt(roofCost)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
